/**
 * PrintReportDocument — overlay d'aperçu + génération d'une SÉLECTION de
 * rapports (unitaire ou combinée), au format choisi :
 *   mode='pdf'  : aperçu + bouton Imprimer (→ PDF via dialogue navigateur)
 *   mode='html' : capture offscreen → fichier .html autonome → download → close
 *   mode='pptx' : capture de chaque section → 1 slide → fichier .pptx → close
 *   mode='xlsx' : extraction des tables rendues → classeur propre → close
 *
 * Monte les composants de la sélection (`reports` = clés du registre) dans
 * l'ordre du registre. Chaque rapport conserve son format A4 paysage ; la
 * combinaison est une concaténation avec saut de page entre sections.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
/* LE MOTEUR DE DIAPOSITIVES ET LA CAPTURE D'ÉCRAN SE CHARGENT AU CLIC
   (2026-09-18) : 372 ko qui ne servent qu'au bouton PowerPoint, et que toute
   l'application portait depuis sa première page. Même patron que l'export
   Excel. */
let pptxLib = null;
function loadPptx() {
  if (!pptxLib) {
    pptxLib = Promise.all([import('pptxgenjs'), import('html2canvas')])
      .then(([p, h]) => ({ pptxgen: p.default || p, html2canvas: h.default || h }));
  }
  return pptxLib;
}
import { useP } from '../i18n/useP';
import Logo from '../components/Logo';
import { PRINT_REPORTS, resolvePrintReport } from './printReportsRegistry';
import { exportReportsToXlsx } from './reportsXlsxExport';
import './hr/print/PeoplePrint.css';
import './hr/PeoplePrintDocument.css';

function inlineAllStyles() {
  const chunks = [];
  for (const sheet of Array.from(document.styleSheets)) {
    try {
      const rules = sheet.cssRules;
      if (!rules) continue;
      for (const rule of Array.from(rules)) chunks.push(rule.cssText);
    } catch (_) { /* CORS — skip */ }
  }
  return chunks.join('\n');
}

// `reportProps` — props additionnelles passées à CHAQUE rapport (ex. `cutoff` :
// arrêté d'affichage du hub Rapports, pour que le PDF imprime le même arrêté).
export default function PrintReportDocument({ activeTenant, year, reports, reportProps = null, mode = 'pdf', onClose }) {
  const { t } = useTranslation();
  const p = useP();
  /* L'ordre du document est celui que l'APPELANT demande, pas celui du registre
     (2026-08-26). Le filtre partait du registre : une section qui écrivait
     ['people-cockpit', 'staff-evolution'] recevait l'inverse, parce que le
     registre range l'évolution avant le cockpit. Personne ne pouvait le
     deviner depuis l'appel, et la table des matières héritait du même
     désordre. Une clé inconnue du registre est simplement ignorée. */
  const selected = useMemo(
    () => reports
      .map(key => PRINT_REPORTS.find(r => r.key === key))
      .filter(Boolean)
      .map(r => resolvePrintReport(r, t)),
    [reports, t],
  );
  const total = selected.length;
  const [doneReports, setDoneReports] = useState(0);
  const [ready, setReady] = useState(false);
  const printRef = useRef(null);

  const yr = year || new Date().getFullYear();
  const editedAt = useMemo(() => new Date(), []);

  useEffect(() => {
    document.body.classList.add('people-printing');
    return () => document.body.classList.remove('people-printing');
  }, []);

  useEffect(() => {
    const handler = () => onClose();
    window.addEventListener('afterprint', handler);
    return () => window.removeEventListener('afterprint', handler);
  }, [onClose]);

  useEffect(() => {
    const t = setTimeout(() => setReady(true), 6000);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (total > 0 && doneReports >= total) setReady(true);
  }, [doneReports, total]);

  // Mode HTML : dès que prêt, capturer + télécharger + fermer.
  useEffect(() => {
    if (mode !== 'html' || !ready) return;
    const el = printRef.current;
    if (!el) return;
    const css = inlineAllStyles();
    const title = `Palestre — ${activeTenant?.name || ''} — ${yr}`;
    const doc = `<!DOCTYPE html><html lang="fr"><head><meta charset="utf-8">
<title>${title}</title><style>${css}
  body{background:#fff;margin:0;padding:24px;}
  .ppl-print-overlay,.ppl-print-controls{position:static!important;background:#fff!important;}
  .ppl-print-controls{display:none!important;}
  .ppl-print__preview{background:#fff!important;padding:0!important;}
  @media print{
    body{padding:0;}
    @page{ size:297mm 210mm; margin:12mm 14mm 16mm; }
    @page{
      @bottom-right{ content:counter(page) " / " counter(pages); font-family:'Inter',Helvetica,Arial,sans-serif; font-size:8pt; color:#777; }
      @bottom-left{ content:"Palestre"; font-family:'Inter',Helvetica,Arial,sans-serif; font-size:8pt; color:#9a8c78; letter-spacing:0.08em; }
    }
  }
</style></head><body><div class="ppl-print">${el.innerHTML}</div></body></html>`;
    const blob = new Blob([doc], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Palestre_${activeTenant?.name || 'tenant'}_${yr}.html`.replace(/\s+/g, '_');
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    const t = setTimeout(() => onClose && onClose(), 200);
    return () => clearTimeout(t);
  }, [mode, ready]); // eslint-disable-line

  // Mode PPTX : capturer chaque section rendue → 1 slide par section.
  useEffect(() => {
    if (mode !== 'pptx' || !ready) return;
    const el = printRef.current;
    if (!el) return;
    let cancelled = false;
    (async () => {
      try {
        const sections = Array.from(el.querySelectorAll('.ppl-print__section'));
        const { pptxgen, html2canvas } = await loadPptx();
        const pptx = new pptxgen();
        const W = 11.69, H = 8.27;            // A4 paysage (pouces)
        pptx.defineLayout({ name: 'A4L', width: W, height: H });
        pptx.layout = 'A4L';
        const margin = 0.3;
        const maxW = W - 2 * margin, maxH = H - 2 * margin;
        // 1re passe : rendre les canvases (certaines sections peuvent échouer).
        const canvases = [];
        for (const sec of sections) {
          try {
            canvases.push(await html2canvas(sec, { scale: 2, backgroundColor: '#ffffff', useCORS: true }));
          } catch (_) { /* une section illisible ne casse pas le deck */ }
          if (cancelled) return;
        }
        const totalSlides = canvases.length;
        canvases.forEach((canvas, i) => {
          const aspect = canvas.width / canvas.height;
          let w = maxW, h = w / aspect;
          if (h > maxH) { h = maxH; w = h * aspect; }
          const slide = pptx.addSlide();
          slide.addImage({
            data: canvas.toDataURL('image/png'),
            x: (W - w) / 2, y: (H - h) / 2, w, h,
          });
          // Numéro de slide (bas-droite) — équivalent du compteur PDF.
          slide.addText(`${i + 1} / ${totalSlides}`, {
            x: W - 1.4, y: H - 0.42, w: 1.1, h: 0.28,
            align: 'right', fontSize: 9, color: '777777',
            fontFace: 'Arial',
          });
        });
        await pptx.writeFile({ fileName: `Palestre_${(activeTenant?.name || 'tenant')}_${yr}.pptx`.replace(/\s+/g, '_') });
      } catch (_) { /* ignore */ }
      if (!cancelled && onClose) onClose();
    })();
    return () => { cancelled = true; };
  }, [mode, ready]); // eslint-disable-line

  // Mode XLSX : dès que prêt, extraire les tables rendues → classeur → close.
  useEffect(() => {
    if (mode !== 'xlsx' || !ready) return;
    const el = printRef.current;
    if (!el) return;
    let cancelled = false;
    (async () => {
      try {
        await exportReportsToXlsx(el, { tenantName: activeTenant?.name, year: yr });
      } catch (_) { /* ignore */ }
      if (!cancelled && onClose) onClose();
    })();
    return () => { cancelled = true; };
  }, [mode, ready]); // eslint-disable-line

  const handleDataReady = () => setDoneReports(n => n + 1);
  const handlePrint = () => window.print();

  const footerLeft = `${(activeTenant?.name || '').toUpperCase()} · ${p('Imprimé le')} ${editedAt.toLocaleDateString('fr-CH', { day: '2-digit', month: '2-digit', year: 'numeric' })}`;
  const cssVarStyle = { '--ppl-footer-left': `"${footerLeft.replace(/"/g, '\\"')}"` };

  const body = (
    <div className="ppl-print" ref={printRef}>
      {/* ── Page de garde unique et distincte ── */}
      <section className="ppl-print__section ppl-print__cover">
        <div className="ppl-cover__inner">
          {/* Marque : péristyle + wordmark, éditeur en pied de page de garde. */}
          <div className="ppl-cover__brandblock">
            <Logo size={34} className="ppl-cover__logo" />
            <div className="ppl-cover__brand">Palestre</div>
          </div>
          <div className="ppl-cover__publisher">{p('un logiciel édité par')} <strong>MyFinancePartner</strong></div>
          <h1 className="ppl-cover__title">{activeTenant?.name || ''}</h1>
          <div className="ppl-cover__sub">{p('Rapport financier')} · {yr}</div>
          {/* Sommaire : les parties du document, une par ligne (les rapports
              composites déclarent leurs `parts` dans le registre). La date
              vit sur chaque page (« Imprimé le ») — pas de doublon ici. */}
          <ul className="ppl-cover__list ppl-cover__toc">
            {selected
              .flatMap(r => (r.parts ? r.parts.map(x => p(x)) : [r.label]))
              .map((lbl, i) => <li key={i} className="ppl-cover__toc-item">{lbl}</li>)}
          </ul>
        </div>
      </section>

      {selected.map(r => {
        const C = r.Component;
        const yearProps = { [r.yearProp]: yr };
        const node = (
          <C
            activeTenant={activeTenant}
            editedAt={editedAt}
            onDataReady={handleDataReady}
            {...yearProps}
            {...(r.props || {})}
            {...(reportProps || {})}
          />
        );
        // En mode xlsx, on enveloppe chaque rapport pour mapper tables → onglet.
        // Les autres modes restent inchangés (pas de wrapper → mise en page intacte).
        return mode === 'xlsx'
          ? <div key={r.key} data-report-key={r.key} data-report-label={r.label}>{node}</div>
          : React.cloneElement(node, { key: r.key });
      })}
    </div>
  );

  // Portalé dans <body> → l'overlay devient frère de #root, ce qui permet de
  // masquer #root (display:none) en @media print sans réserver d'espace (sinon
  // l'app cachée en visibility:hidden produisait des pages blanches en tête).
  return createPortal(
    <div className="ppl-print-overlay" style={cssVarStyle}>
      {mode === 'pdf' && (
        <div className="ppl-print-controls">
          <div className="ppl-print-controls__left">
            <span className="ppl-print-controls__title">
              {p('Aperçu —')} {total} {total > 1 ? p('rapports') : p('rapport')} · {activeTenant?.name}
            </span>
            {!ready && (
              <span className="ppl-print-controls__loading">
                ⟳ {t('common:status.loading').replace(/…$/, '')} ({doneReports}/{total})…
              </span>
            )}
          </div>
          <div className="ppl-print-controls__right">
            <button
              className="ppl-print-btn ppl-print-btn--print"
              onClick={handlePrint}
              disabled={!ready}
              title={ready ? p('Imprimer ou sauvegarder en PDF') : p('Patientez…')}
            >
              {ready ? `⎙ ${p('Imprimer / PDF')}` : `⟳ ${t('common:status.loading')}`}
            </button>
            <button className="ppl-print-btn ppl-print-btn--close" onClick={onClose}>
              ✕ {t('common:actions.close')}
            </button>
          </div>
        </div>
      )}
      <div className="ppl-print__preview">{body}</div>
    </div>,
    document.body,
  );
}
